import React, { useContext, useState, useEffect } from "react";

import {
    styled,
    Card,
    Grid,
    Button
} from '@mui/material';

import { DataGrid, GridPaginationMeta, useGridApiRef } from '@mui/x-data-grid';

import { useFormik } from 'formik';

import { useAppContextFeatures } from "./AppContext";

import { useAuthAccess } from "../hooks/useAuthAccess";
import { useSystemPrompt } from "../hooks/useSystemPrompt";
import { useSystemRoutes } from "../hooks/useSystemRoutes";
import { useTranslations } from "../hooks/useTranslations";
import { useReportQuery, reportTypes } from "../hooks/useReportQuery";

import LocationSelector from "../components/selector/LocationSelector"

import { mergeSchema } from "../utils/dataSchema";
import { spliceString } from "../utils/formatString";

const ReportContext = React.createContext();

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(0)
}));

export function useReportContext() {
    return useContext(ReportContext);
}

export function ReportContextProvider({
    routeType,
    reportScope,
    reportType,
    validationSchema,
    advancedValidationSchema,
    initialValues,
    advancedInitialValues,
    children
}) {

    // const apiRef = useGridApiRef();

    const { isLoading, onSubmit } = useReportQuery({ reportType, reportScope })

    const { t } = useTranslations({ ns: ["common", "@report", "prompt", "@document", "sidebar"] })

    const {
        setSystemLoading,
        getLocationIds,
        setLocationIds,
        openCustomDrawer
    } = useAppContextFeatures()

    const { authData, getAvailableLocations } = useAuthAccess()
    const { promptMessage } = useSystemPrompt()
    const { getRoute } = useSystemRoutes(routeType)

    const [reportData, setReportData] = useState([])

    // available reports
    const availableReports = reportTypes[reportScope]
    const availableReportScope = reportScope

    const [selectedLocationIds, setSelectedLocationIds] = useState()

    const availableLocations = getAvailableLocations();

    const [paginationModel, setPaginationModel] = useState({
        started: false,
        page: 0,
        pageSize: 50,
        rowCount: 0
    });

    const validateLocationSelection = () => {
        const selected = getLocationIds()
        return selected && selected.length > 0
    }

    // check the location if more than 1 then select the first one by default
    useEffect(() => {

        const currentLocationIds = getLocationIds();

        const selected = [];

        if (currentLocationIds && currentLocationIds.length > 0) {

            currentLocationIds.map(locationId => {

                // console.log('currentLocationIds', currentLocationIds, availableLocations)

                // get the location information
                const loc = availableLocations.filter(i => i.id === locationId)

                if (loc.length) {
                    selected.push(loc[0])
                }

                return false;

            })
        }

        setSelectedLocationIds(selected)

    }, [getLocationIds])


    const fetchReportData = async ({ values, paginationModel }) => {

        setSystemLoading(true)

        try {
            let { data } = await onSubmit({
                values,
                paginationModel
            })

            setReportData(data?.rows)

            setPaginationModel({
                ...paginationModel,
                started: data.total > 0 ? Boolean(true): Boolean(false),
                rowCount: data?.total
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const formikOnSubmit = async (values) => {

        await fetchReportData({ values, paginationModel })

    }

    const handlePaginationModelChange = (pgModel, b) => {

        // console.log('pgModel', pgModel)

        let newPaginationModel = {
            ...paginationModel,
            page: pgModel.page,
            pageSize: pgModel.pageSize
        }

        setPaginationModel(newPaginationModel)

        fetchReportData({ values, paginationModel: newPaginationModel })

    }

    const formik = useFormik({
        initialValues: { ...initialValues, ...advancedInitialValues },
        validationSchema: mergeSchema(validationSchema, advancedValidationSchema),
        // onSubmit: (values) => { console.log('values', values) }
        onSubmit: formikOnSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderLocationSelector = ({ callback }) => {
        return (
              <LocationSelector
                    config={{
                        title: t("selectSearchLocation"),
                        description: t("youCanSelectMultipleWarehouseLocationsToProceed")
                    }}
                    handleOnNext={({ selected }) => {

                        if (callback) {
                            callback({ selected })
                        }
                        // setData([])
                        setLocationIds(selected.map(i => i.id))

                    }}
              />
        )
    }

    const RenderReportData = ({ columns }) => {

        if (!reportData) return <></>
        if (reportData.length <= 0) return <></>

        const getRowId = (row) => {
            return row.no;
        }

        return (
              <StyledCard>
                  <DataGrid
                        // apiRef={apiRef}
                        getRowId={getRowId}
                        rows={reportData}
                        columns={columns}
                        disableColumnMenu
                        rowHeight={30}
                        initialState={{
                            pagination: {
                                paginationModel
                            },
                        }}
                        rowCount={paginationModel.rowCount}
                        onPaginationModelChange={handlePaginationModelChange}
                        // paginationMeta={paginationMeta}
                        loading={isLoading}
                        pageSizeOptions={[paginationModel.pageSize, 100]}
                        // paginationModel={paginationModel}
                        paginationMode="server"
                        // onPaginationModelChange={setPaginationModel}
                  />
              </StyledCard>
        )
    }

    const RenderToolbar = ({ handleExport }) => {

        let reportStarted = paginationModel.started

        return (
              <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                  <Grid item md={6} textAlign={"left"}>
                      {/* {JSON.stringify(paginationModel)} */}
                      <Button
                            disabled={reportStarted}
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                      >
                          {t('startQuery')}
                      </Button>
                      {typeof handleExport === "function" && (
                            <Button
                                  disabled={!reportStarted}
                                  variant="contained"
                                  color="primary"
                                  onClick={handleExport}
                                  sx={{
                                      mt: 3,
                                      mb: 2,
                                      mr: 1
                                  }}
                            >
                                {t('downloadExcel')}
                            </Button>)}
                  </Grid>
                  <Grid item md={6} textAlign={"right"}>
                      <Button
                            disabled={!reportStarted}
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            onClick={() => {
                                setPaginationModel({
                                    ...paginationModel,
                                    started: false
                                })
                                setReportData([])
                            }}
                      >
                          {t('resetFilter')}
                      </Button>
                      {/* <Button
                        disabled={!reportStarted}
                        type="submit"
                        variant="contained"
                        sx={{
                            mt: 3,
                            mb: 2,
                            ml: 1
                        }}
                        onClick={() => {
                            // setPaginationModel({
                            //     ...paginationModel,
                            //     started: false
                            // })
                            // setReportData([])
                        }}
                    >
                        {t('exportToExcel')}
                    </Button> */}
                  </Grid>
              </Grid>
        )
    }

    const handleExport = async ({ generateExcel, formik, excelFileName }) => {
        setSystemLoading(true)
        let response = await generateExcel({
            filterValues: formik.values,
            config: {
                generateFileName: excelFileName
            }
        });
        setSystemLoading(false)
    };

    return (
          <ReportContext.Provider
                value={{
                    t,
                    formik,
                    routeType,
                    authData,
                    availableReports,
                    availableReportScope,
                    getAvailableLocations,
                    availableLocations,
                    reportData,
                    disableSearchInput: paginationModel.started,

                    setSystemLoading,
                    promptMessage,
                    getRoute,

                    validateLocationSelection,
                    getLocationIds,
                    setLocationIds,
                    selectedLocationIds,
                    setSelectedLocationIds,
                    RenderLocationSelector,
                    RenderReportData,
                    RenderToolbar,
                    handleExport,
                    openCustomDrawer,
                    spliceString

                }}>

              {children}

          </ReportContext.Provider>
    )

}